import { Info } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import {
  calculateJdFeeRatioPlan,
  cumulativeFeeRatioRows,
  monthToDateAverage,
  recentSevenDayAverage,
  type JdFeeRatioPayload,
} from '../jdFeeRatio'

type ForecastMode = 'month-to-date' | 'recent-seven' | 'custom'

interface StoredFeeRatioForecast {
  schemaVersion: 1
  dailyForecastGmv: number
  savedAt: string
}

const feeRatioForecastStorageKey = (payload: Pick<JdFeeRatioPayload, 'storeId' | 'month'>) =>
  `jd-fee-ratio:daily-forecast:${payload.storeId}:${payload.month}`

function readStoredFeeRatioForecast(payload: Pick<JdFeeRatioPayload, 'storeId' | 'month'>) {
  try {
    const raw = window.localStorage.getItem(feeRatioForecastStorageKey(payload))
    if (!raw) return null
    const stored = JSON.parse(raw) as Partial<StoredFeeRatioForecast>
    return stored.schemaVersion === 1
      && typeof stored.dailyForecastGmv === 'number'
      && Number.isFinite(stored.dailyForecastGmv)
      && stored.dailyForecastGmv >= 0
      ? stored.dailyForecastGmv
      : null
  } catch {
    return null
  }
}

const money = new Intl.NumberFormat('zh-CN', {
  style: 'currency',
  currency: 'CNY',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

function formatMoney(value: number) {
  return money.format(value).replace('CN¥', '¥')
}

function formatPercent(value: number | null) {
  return value == null ? '—' : `${(value * 100).toFixed(2)}%`
}

function formatMonth(value: string) {
  const [year, month] = value.split('-')
  return `${year}年${Number(month)}月`
}

function formatDay(value: string) {
  const [, month, day] = value.split('-')
  return `${Number(month)}月${Number(day)}日`
}

function FeeRatioPanelHeading({ title, description, children }: { title: string; description: string; children?: React.ReactNode }) {
  return (
    <header className="fee-ratio-panel-heading">
      <div><h2>{title}</h2><p>{description}</p></div>
      {children}
    </header>
  )
}

function FeeRatioLoading({ error }: { error?: string }) {
  return (
    <div className={error ? 'error-state' : 'loading-state'}>
      <h1>{error ? '费比数据暂不可用' : '正在计算本月费比…'}</h1>
      <p>{error || '正在汇总成交金额、京准通和品专合同。'}</p>
    </div>
  )
}

export function JdFeeRatioPage() {
  const [payload, setPayload] = useState<JdFeeRatioPayload | null>(null)
  const [error, setError] = useState('')
  const [forecastMode, setForecastMode] = useState<ForecastMode>('month-to-date')
  const [dailyForecastInput, setDailyForecastInput] = useState('')
  const [savedDailyForecastGmv, setSavedDailyForecastGmv] = useState<number | null>(null)
  const [forecastStorageMessage, setForecastStorageMessage] = useState('')
  const [showAllActuals, setShowAllActuals] = useState(false)

  useEffect(() => {
    let active = true
    fetch('/data/jd-fee-ratio.json', { cache: 'no-store' })
      .then(async (response) => {
        const body = await response.json() as JdFeeRatioPayload & { error?: string }
        if (!response.ok) throw new Error(body.error || `费比数据加载失败：${response.status}`)
        if (active) {
          const storedForecast = readStoredFeeRatioForecast(body)
          setPayload(body)
          setDailyForecastInput((storedForecast ?? monthToDateAverage(body)).toFixed(2))
          setForecastMode(storedForecast == null ? 'month-to-date' : 'custom')
          setSavedDailyForecastGmv(storedForecast)
          setForecastStorageMessage(storedForecast == null ? '' : '已读取本机保存值')
        }
      })
      .catch((reason: unknown) => active && setError(reason instanceof Error ? reason.message : String(reason)))
    return () => { active = false }
  }, [])

  const actualRows = useMemo(() => payload ? cumulativeFeeRatioRows(payload) : [], [payload])
  const monthAverage = payload ? monthToDateAverage(payload) : 0
  const sevenDayAverage = payload ? recentSevenDayAverage(payload) : 0
  const forecastInputValue = Number(dailyForecastInput)
  const hasValidForecastInput = dailyForecastInput.trim() !== '' && Number.isFinite(forecastInputValue) && forecastInputValue >= 0
  const dailyForecastGmv = hasValidForecastInput ? forecastInputValue : 0
  const plan = useMemo(
    () => payload ? calculateJdFeeRatioPlan(payload, dailyForecastGmv) : null,
    [dailyForecastGmv, payload],
  )

  if (error) return <FeeRatioLoading error={error} />
  if (!payload || !plan) return <FeeRatioLoading />

  const targetPercent = payload.target.effectiveRate * 100
  const chartRows = actualRows.map((row) => ({
    ...row,
    dateLabel: formatDay(row.date),
    dayRatePercent: row.dailyRate == null ? null : row.dailyRate * 100,
    cumulativeRatePercent: row.cumulativeRate == null ? null : row.cumulativeRate * 100,
  }))
  const maxChartRate = Math.max(
    12,
    targetPercent,
    ...chartRows.flatMap((row) => [row.dayRatePercent || 0, row.cumulativeRatePercent || 0]),
  )
  const chartCeiling = Math.ceil(maxChartRate / 3) * 3
  const visibleActualRows = showAllActuals ? [...actualRows].reverse() : actualRows.slice(-7).reverse()
  const needsCatchup = (payload.actual.rate || 0) < payload.target.effectiveRate
  const benchmarkMonthName = `${Number(payload.target.benchmarkMonth.slice(5))}月`
  const isSavedForecast = hasValidForecastInput && savedDailyForecastGmv != null && Math.abs(savedDailyForecastGmv - dailyForecastGmv) < 0.005
  const isDefaultForecast = hasValidForecastInput && Math.abs(monthAverage - dailyForecastGmv) < 0.005

  const saveForecastToBrowser = () => {
    if (!hasValidForecastInput || payload.remainingDays === 0) return
    const stored: StoredFeeRatioForecast = {
      schemaVersion: 1,
      dailyForecastGmv,
      savedAt: new Date().toISOString(),
    }
    try {
      window.localStorage.setItem(feeRatioForecastStorageKey(payload), JSON.stringify(stored))
      setDailyForecastInput(dailyForecastGmv.toFixed(2))
      setSavedDailyForecastGmv(dailyForecastGmv)
      setForecastMode('custom')
      setForecastStorageMessage('已保存，仅当前浏览器生效')
    } catch {
      setForecastStorageMessage('保存失败，请检查浏览器存储权限')
    }
  }

  const restoreDefaultForecast = () => {
    try {
      window.localStorage.removeItem(feeRatioForecastStorageKey(payload))
      setDailyForecastInput(monthAverage.toFixed(2))
      setSavedDailyForecastGmv(null)
      setForecastMode('month-to-date')
      setForecastStorageMessage('已恢复本月至今日均')
    } catch {
      setForecastStorageMessage('恢复失败，请检查浏览器存储权限')
    }
  }

  return (
    <div className="jd-fee-ratio-page page-stack">
      <section className="fee-ratio-title-row">
        <div>
          <div className="fee-ratio-title-line">
            <h1>京东费比达标</h1>
            <span>数据截至 {payload.dataThrough}</span>
          </div>
          <p>按月判断费比是否达标，并把剩余广告投入拆到每一天</p>
        </div>
        <select aria-label="月份" value={payload.month} onChange={() => undefined}>
          <option value={payload.month}>{formatMonth(payload.month)}</option>
        </select>
      </section>

      <section className="fee-ratio-summary">
        <article>
          <span>本月累计费比</span>
          <strong>{formatPercent(payload.actual.rate)}</strong>
          <div><span>累计成交<b>{formatMoney(payload.actual.gmv)}</b></span><span>考核广告费<b>{formatMoney(payload.actual.assessmentSpend)}</b></span></div>
        </article>
        <article>
          <span>本月有效目标</span>
          <strong>{formatPercent(payload.target.effectiveRate)}<em className={needsCatchup ? 'is-warning' : 'is-achieved'}>{needsCatchup ? '需追赶' : '已达标'}</em></strong>
          <p>MAX（平台最低{formatPercent(payload.target.platformMinimumRate)}，2025年{benchmarkMonthName}同月费比{formatPercent(payload.target.benchmarkRate)}）</p>
          <p>按当前已成交金额，累计缺口 <b>{formatMoney(plan.currentGap)}</b></p>
        </article>
        <article className="fee-ratio-summary-primary">
          <div>
            <span>月底达标还需投常规京准通</span>
            <strong>{formatMoney(plan.remainingRegularJzt)}</strong>
            <p>已扣除未来固定品专；随成交预测实时重算</p>
          </div>
          <aside>
            <span>剩余考核广告费</span>
            <b>{formatMoney(plan.remainingAssessmentSpend)}</b>
            <small>剩余日期需做到 {formatPercent(plan.requiredRemainingRate)}</small>
          </aside>
        </article>
      </section>

      <section className="fee-ratio-panel">
        <FeeRatioPanelHeading title="考核广告费构成" description="实际投放与固定摊销分开，精选联盟不进入费比" />
        <div className="fee-ratio-cost-strip">
          <article><i className="is-blue" /><div><span>常规京准通（截至{formatDay(payload.dataThrough)}）</span><strong>{formatMoney(payload.actual.regularJzt)}</strong><small>计入考核，可灵活投放</small></div></article>
          <article><i className="is-amber" /><div><span>搜索品专（{payload.brandAllocation.quarter}固定摊销）</span><strong>{formatMoney(payload.brandAllocation.dailyAmount)} / 天</strong><small>季度{formatMoney(payload.brandAllocation.amount)} ÷ {payload.brandAllocation.dayCount}天</small></div></article>
          <article><i className="is-gray" /><div><span>精选联盟</span><strong>不计入</strong><small>页面不参与费比计算与追赶预算</small></div></article>
        </div>
      </section>

      <div className="fee-ratio-notice"><Info aria-hidden="true" /><span>预测不是预算承诺：每天新数据入库后自动重算。真正需要手动控制的是“常规京准通”，搜索品专始终按季度合同固定摊销。</span></div>

      <div className="fee-ratio-workspace">
        <section className="fee-ratio-panel fee-ratio-actuals">
          <FeeRatioPanelHeading title="每日费比走势" description="柱为单日费比，折线为累计费比，虚线为本月有效目标" />
          <div className="fee-ratio-chart-legend"><span><i className="bar" />单日费比</span><span><i className="line" />累计费比</span><span><i className="target" />目标{formatPercent(payload.target.effectiveRate)}</span></div>
          <div className="fee-ratio-chart">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartRows} margin={{ top: 8, right: 14, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#eaeff5" vertical={false} />
                <XAxis dataKey="dateLabel" tick={{ fontSize: 10, fill: '#98a2b3' }} tickLine={false} axisLine={false} interval={1} />
                <YAxis domain={[0, chartCeiling]} ticks={Array.from({ length: Math.floor(chartCeiling / 3) + 1 }, (_, index) => index * 3)} tickFormatter={(value) => `${value}%`} tick={{ fontSize: 10, fill: '#98a2b3' }} tickLine={false} axisLine={false} width={38} />
                <Tooltip formatter={(value, name) => [`${Number(value).toFixed(2)}%`, name === 'dayRatePercent' ? '单日费比' : '累计费比']} labelFormatter={(label) => String(label)} />
                <ReferenceLine y={targetPercent} stroke="#f79009" strokeDasharray="6 5" />
                <Bar dataKey="dayRatePercent" fill="#dbeafe" radius={[2, 2, 0, 0]} maxBarSize={18} isAnimationActive={false} />
                <Line dataKey="cumulativeRatePercent" stroke="#0f66f5" strokeWidth={2.2} dot={{ r: 3, fill: '#fff', strokeWidth: 2 }} activeDot={{ r: 4 }} isAnimationActive={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="fee-ratio-table-title"><h3>每日数据明细</h3><button type="button" onClick={() => setShowAllActuals((value) => !value)}>{showAllActuals ? '仅看最近7天' : `查看全部${actualRows.length}天`}</button></div>
          <div className="fee-ratio-table-wrap fee-ratio-actual-table"><table><thead><tr><th>日期</th><th>成交金额</th><th>常规京准通实际消耗</th><th>品专摊销</th><th>单日费比</th><th>累计费比</th></tr></thead><tbody>
            {visibleActualRows.map((row) => <tr key={row.date}><td>{formatDay(row.date)}</td><td>{formatMoney(row.gmv)}</td><td>{formatMoney(row.regularJzt)}</td><td>{formatMoney(row.brandAllocated)}</td><td className={(row.dailyRate || 0) >= payload.target.effectiveRate ? 'is-achieved' : 'is-warning'}>{formatPercent(row.dailyRate)}</td><td className={(row.cumulativeRate || 0) >= payload.target.effectiveRate ? 'is-achieved' : 'is-warning'}>{formatPercent(row.cumulativeRate)}</td></tr>)}
          </tbody></table></div>
        </section>

        <section className="fee-ratio-panel fee-ratio-planning">
          <FeeRatioPanelHeading title="剩余天数投放计划" description={`${formatDay(plan.rows[0]?.date || payload.dataThrough)}—${formatDay(plan.rows.at(-1)?.date || payload.dataThrough)} · 每天按最新完成数据重新滚动`}>
            <div className="fee-ratio-forecast-controls">
              <label className="fee-ratio-forecast-input">
                <span>剩余天数预计日均成交金额</span>
                <span className="fee-ratio-forecast-input-shell">
                  <b>¥</b>
                  <input
                    aria-label="剩余天数预计日均成交金额"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={dailyForecastInput}
                    disabled={payload.remainingDays === 0}
                    onChange={(event) => {
                      setDailyForecastInput(event.target.value)
                      setForecastMode('custom')
                      setForecastStorageMessage('')
                    }}
                    onBlur={() => hasValidForecastInput && setDailyForecastInput(dailyForecastGmv.toFixed(2))}
                  />
                  <small>元/天</small>
                </span>
              </label>
              <div className="fee-ratio-forecast-shortcuts">
                <span>{forecastMode === 'custom' ? `当前：${isSavedForecast ? '已保存值' : '自定义'}` : forecastMode === 'recent-seven' ? '当前：近7日均值' : '当前：本月至今日均'}</span>
                <div role="group" aria-label="预计成交快捷估算">
                  <button type="button" aria-pressed={forecastMode === 'month-to-date'} onClick={() => { setDailyForecastInput(monthAverage.toFixed(2)); setForecastMode('month-to-date'); setForecastStorageMessage('') }}>本月至今日均</button>
                  <button type="button" aria-pressed={forecastMode === 'recent-seven'} onClick={() => { setDailyForecastInput(sevenDayAverage.toFixed(2)); setForecastMode('recent-seven'); setForecastStorageMessage('') }}>近7日均值</button>
                </div>
              </div>
              <div className="fee-ratio-forecast-actions">
                <span>{forecastStorageMessage || '仅保存到当前浏览器，不影响其他电脑'}</span>
                <div>
                  <button className="is-save" type="button" disabled={!hasValidForecastInput || payload.remainingDays === 0 || isSavedForecast} onClick={saveForecastToBrowser}>{isSavedForecast ? '已保存' : '保存'}</button>
                  <button className="is-reset" type="button" disabled={savedDailyForecastGmv == null && isDefaultForecast} onClick={restoreDefaultForecast}>恢复默认</button>
                </div>
              </div>
            </div>
          </FeeRatioPanelHeading>
          <div className="fee-ratio-plan-summary">
            <div><span>剩余天数</span><strong>{payload.remainingDays}天</strong></div>
            <div><span>预计剩余成交</span><strong>{formatMoney(plan.forecastRemainingGmv)}</strong></div>
            <div><span>预计全月成交</span><strong>{formatMoney(plan.forecastMonthGmv)}</strong></div>
            <div><span>剩余品专固定摊销</span><strong>{formatMoney(plan.remainingBrandAllocated)}</strong></div>
            <div className="is-primary"><span>日均常规京准通</span><strong>{formatMoney(plan.dailyRegularJzt)}</strong></div>
          </div>
          <div className="fee-ratio-table-wrap fee-ratio-plan-table"><table><thead><tr><th>日期</th><th>预计成交</th><th>固定品专</th><th>当天常规京准通需消耗</th><th>预计累计费比</th></tr></thead><tbody>
            {plan.rows.map((row) => <tr key={row.date}><td>{formatDay(row.date)}</td><td>{formatMoney(row.forecastGmv)}</td><td>{formatMoney(row.brandAllocated)}</td><td className="is-required">{formatMoney(row.regularJztRequired)}</td><td className={(row.projectedCumulativeRate || 0) >= payload.target.effectiveRate - 0.000001 ? 'is-achieved' : 'is-warning'}>{formatPercent(row.projectedCumulativeRate)}</td></tr>)}
            {!plan.rows.length ? <tr><td colSpan={5} className="fee-ratio-empty">本月已结束，没有剩余日期需要安排。</td></tr> : null}
          </tbody></table></div>
        </section>
      </div>

      <section className="fee-ratio-panel fee-ratio-method">
        <div><h2>数据说明与计算口径</h2><ol>
          <li>费比 =（常规京准通 + 搜索品专按日固定摊销）÷ 成交金额；精选联盟不计入。</li>
          <li>本月目标 = MAX（7%，2025年同月费比）；2025年{benchmarkMonthName}费比 =（{formatMoney(payload.target.benchmarkRegularJzt)} + {formatMoney(payload.target.benchmarkBrandAllocated)}）÷ {formatMoney(payload.target.benchmarkGmv)} = {formatPercent(payload.target.benchmarkRate)}。</li>
          <li>剩余常规京准通 = 预计全月成交 × 目标费比 − 已发生考核广告费 − 剩余品专固定摊销。</li>
        </ol></div>
        <div><span>销售：京东商智商品成交口径</span><span>广告：京准通财务记录、搜索品专合同</span><span>数据时间：{payload.dataThrough}</span></div>
      </section>
    </div>
  )
}
